import { useMemo, useRef } from "react";

type TimeSliderProps = {
  minutesOfDay: number;
  sunriseMinutes: number;
  sunsetMinutes: number;
  onChange: (nextMinutesOfDay: number) => void;
};

const MINUTES_IN_HOUR = 60;
const MIN_TIME_MINUTES = 0;
const MAX_TIME_MINUTES = 24 * MINUTES_IN_HOUR;
const STEP_MINUTES = 15;
const TICK_WIDTH_PX = 10;
const DRAG_PX_PER_STEP = 8;

type DragState = {
  pointerId: number;
  startX: number;
  startMinutes: number;
};

function clampMinutesOfDay(value: number) {
  return Math.max(MIN_TIME_MINUTES, Math.min(MAX_TIME_MINUTES, value));
}

function snapToStep(value: number) {
  return Math.round(value / STEP_MINUTES) * STEP_MINUTES;
}

function formatMinutesOfDay(value: number) {
  const clamped = clampMinutesOfDay(value);
  if (clamped === MAX_TIME_MINUTES) {
    return "24:00";
  }

  const hours = Math.floor(clamped / MINUTES_IN_HOUR);
  const minutes = clamped % MINUTES_IN_HOUR;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

export function TimeSlider({ minutesOfDay, sunriseMinutes, sunsetMinutes, onChange }: TimeSliderProps) {
  const dragStateRef = useRef<DragState | null>(null);
  const normalizedMinutes = clampMinutesOfDay(snapToStep(minutesOfDay));
  const normalizedSunriseMinutes = clampMinutesOfDay(snapToStep(sunriseMinutes));
  const normalizedSunsetMinutes = clampMinutesOfDay(snapToStep(sunsetMinutes));
  const hasDaylightWindow = normalizedSunriseMinutes < normalizedSunsetMinutes;
  const sunriseOffsetPx = ((normalizedSunriseMinutes - MIN_TIME_MINUTES) / STEP_MINUTES) * TICK_WIDTH_PX;
  const sunsetOffsetPx = ((normalizedSunsetMinutes - MIN_TIME_MINUTES) / STEP_MINUTES) * TICK_WIDTH_PX;
  const ticks = useMemo(() => {
    const values: number[] = [];
    for (let value = MIN_TIME_MINUTES; value <= MAX_TIME_MINUTES; value += STEP_MINUTES) {
      values.push(value);
    }
    return values;
  }, []);
  const trackWidthPx = ticks.length * TICK_WIDTH_PX;

  const selectedTickIndex = (normalizedMinutes - MIN_TIME_MINUTES) / STEP_MINUTES;

  const updateFromDrag = (deltaX: number) => {
    const state = dragStateRef.current;
    if (!state) return;

    const deltaSteps = Math.round(deltaX / DRAG_PX_PER_STEP);
    const nextMinutes = clampMinutesOfDay(snapToStep(state.startMinutes - deltaSteps * STEP_MINUTES));
    if (nextMinutes !== normalizedMinutes) {
      onChange(nextMinutes);
    }
  };

  return (
    <div
      className="touch-none select-none space-y-2"
      onPointerDown={event => {
        dragStateRef.current = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startMinutes: normalizedMinutes,
        };
        event.currentTarget.setPointerCapture(event.pointerId);
      }}
      onPointerMove={event => {
        if (dragStateRef.current?.pointerId !== event.pointerId) return;
        updateFromDrag(event.clientX - dragStateRef.current.startX);
      }}
      onPointerUp={event => {
        if (dragStateRef.current?.pointerId !== event.pointerId) return;
        dragStateRef.current = null;
        event.currentTarget.releasePointerCapture(event.pointerId);
      }}
      onPointerCancel={event => {
        if (dragStateRef.current?.pointerId !== event.pointerId) return;
        dragStateRef.current = null;
      }}
    >
      <div className="relative h-16 overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
        <div className="pointer-events-none absolute inset-x-0 top-1.5 z-20 text-center">
          <p className="text-[9px] font-medium uppercase tracking-wide text-slate-500">
            Selected Time: {formatMinutesOfDay(normalizedMinutes)}
          </p>
        </div>
        <div
          className="absolute left-1/2 top-0 h-full"
          style={{
            width: `${trackWidthPx}px`,
            transform: `translateX(-${selectedTickIndex * TICK_WIDTH_PX + TICK_WIDTH_PX / 2}px)`,
          }}
        >
          {hasDaylightWindow ? (
            <>
              <div className="pointer-events-none absolute inset-y-0 left-[-300px] z-0 bg-slate-300" style={{ width: `${sunriseOffsetPx + 300}px` }} />
              <div
                className="pointer-events-none absolute inset-y-0 z-0 bg-slate-300"
                style={{ left: `${sunsetOffsetPx}px`, width: `${Math.max(0, trackWidthPx - sunsetOffsetPx + 300)}px` }}
              />
            </>
          ) : (
            <div className="pointer-events-none absolute inset-0 z-0 bg-slate-900/18" />
          )}
          <div className="relative z-10 flex h-full items-end">
            {ticks.map(tick => {
              const isHourTick = tick % MINUTES_IN_HOUR === 0;
              const isNightTick = hasDaylightWindow
                ? tick < normalizedSunriseMinutes || tick >= normalizedSunsetMinutes
                : true;
              return (
                <div key={tick} className="relative h-full shrink-0" style={{ width: `${TICK_WIDTH_PX}px` }}>
                  <div
                    className={`absolute bottom-3.5 left-1/2 w-px -translate-x-1/2 ${
                      isHourTick
                        ? isNightTick
                          ? "h-[14px] bg-slate-400"
                          : "h-[14px] bg-slate-500"
                        : isNightTick
                          ? "h-1.5 bg-slate-500"
                          : "h-1.5 bg-slate-300"
                    }`}
                  />
                  {isHourTick ? (
                    <span
                      className={`absolute bottom-0.5 left-1/2 z-10 -translate-x-1/2 text-[9px] tabular-nums ${
                        isNightTick ? "text-slate-400" : "text-slate-500"
                      }`}
                    >
                      {String(tick / MINUTES_IN_HOUR).padStart(2, "0")}:00
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>

        <div className="pointer-events-none absolute bottom-0 left-1/2 h-[40px] w-[2px] -translate-x-1/2 bg-amber-500 rounded-t-full" />
      </div>

      <input
        id="time-slider"
        aria-label="Time of day"
        type="range"
        min={MIN_TIME_MINUTES}
        max={MAX_TIME_MINUTES}
        step={STEP_MINUTES}
        value={normalizedMinutes}
        onChange={event => onChange(Number.parseInt(event.target.value, 10))}
        className="sr-only"
      />
    </div>
  );
}
